import axios, { AxiosInstance, AxiosResponse } from 'axios'
import { environment } from '../../config/environment'
import { tokenManager } from '../auth/tokenManager'
import { logger } from '../../utils/logger'

class ApiClient {
  private static instance: ApiClient
  private axiosInstance: AxiosInstance

  private constructor() {
    const config = environment.getConfig()
    
    this.axiosInstance = axios.create({
      baseURL: config.apiBaseUrl,
      timeout: config.apiTimeout,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor - Add Firebase auth token
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        // Get token from TokenManager (handles caching and auth state)
        const token = await tokenManager.getToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        
        logger.log('🌐 API Request:', {
          method: config.method?.toUpperCase(),
          url: config.url,
          baseURL: config.baseURL,
          hasAuth: !!config.headers.Authorization,
        })
        return config
      },
      (error) => {
        logger.error('❌ API Request Error:', error)
        return Promise.reject(error)
      }
    )

    // Response interceptor - Handle 401 and retry with new token
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        logger.log('✅ API Response:', {
          status: response.status,
          url: response.config.url,
          data: response.data,
        })
        return response
      },
      async (error) => {
        const originalRequest = error.config
        
        // If we get a 401 and haven't already retried, try to refresh the token
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true
          
          try {
            // Clear cached token to force refresh
            tokenManager.clearToken()
            // Get a fresh token
            const newToken = await tokenManager.getToken()
            if (newToken) {
              // Update the request header
              originalRequest.headers.Authorization = `Bearer ${newToken}`
              // Retry the request
              return this.axiosInstance(originalRequest)
            }
          } catch (refreshError) {
            logger.error('❌ Failed to refresh token:', refreshError)
          }
        }
        
        logger.error('❌ API Response Error:', {
          status: error.response?.status,
          url: error.config?.url,
          message: error.message,
          data: error.response?.data,
        })
        return Promise.reject(error)
      }
    )
  }

  public static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient()
    }
    return ApiClient.instance
  }

  public get axios(): AxiosInstance {
    return this.axiosInstance
  }

  // Generic methods
  public async get<T>(url: string): Promise<T> {
    const response = await this.axiosInstance.get<T>(url)
    return response.data
  }

  public async post<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.axiosInstance.post<T>(url, data)
    return response.data
  }

  public async put<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.axiosInstance.put<T>(url, data)
    return response.data
  }

  public async delete<T>(url: string): Promise<T> {
    const response = await this.axiosInstance.delete<T>(url)
    return response.data
  }
}

export const apiClient = ApiClient.getInstance()